<template>
	<Dialog
		:visible="visible"
		@update:visible="emit('update:visible', $event)"
		:header="`Editing tooltip for ${label}`"
		modal
		class="i2Dialog"
		style="width: 32rem; max-width: 90vw"
	>
		<div class="flex flex-col gap-3">
			<div class="flex flex-col gap-1">
				<label class="font-medium">Tooltip Text</label>
				<InputText
					ref="tooltipInputRef"
					v-model="tooltipText"
					placeholder="Enter tooltip text (leave blank to remove)"
					class="w-full"
				/>
			</div>
		</div>
		<template #footer>
			<Button label="Cancel" class="p-button-text" @click="cancel" />
			<Button label="Save" class="btn p-button" @click="save" :loading="saving" />
		</template>
	</Dialog>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from "vue"
import Dialog from "primevue/dialog"
import InputText from "primevue/inputtext"
import Button from "primevue/button"
import { useToast } from "primevue/usetoast"
import { HelpLabelClient, UpdateTooltipRequest } from "../../../entities/NSwagGenerated"
import { useHelpLabelStore } from "@/store/helpLabelStore"

const props = defineProps({
	visible: {
		type: Boolean,
		required: true,
	},
	helpElementName: {
		type: String,
		required: true,
	},
	label: {
		type: String,
		required: true,
	},
})

const emit = defineEmits<{
	"update:visible": [value: boolean]
	saved: []
}>()

const toast = useToast()
const helpLabelStore = useHelpLabelStore()
const helpLabelClient = new HelpLabelClient(import.meta.env.VITE_API_URL?.toString())

const tooltipText = ref("")
const saving = ref(false)
const tooltipInputRef = ref<InstanceType<typeof InputText> | null>(null)

watch(
	() => props.visible,
	(isVisible) => {
		if (isVisible) {
			// Load current tooltip value from store (no API call needed)
			tooltipText.value = helpLabelStore.getTooltip(props.helpElementName) ?? ""
			nextTick(() => {
				;(tooltipInputRef.value as any)?.$el?.focus()
			})
		}
	},
	{ immediate: true },
)

function cancel() {
	emit("update:visible", false)
}

async function save() {
	saving.value = true
	try {
		const request = new UpdateTooltipRequest({ tooltipText: tooltipText.value || undefined })
		await helpLabelClient.tooltip(props.helpElementName, request)
		helpLabelStore.updateTooltip(props.helpElementName, tooltipText.value || null)
		toast.add({ severity: "success", summary: "Tooltip saved", life: 2000 })
		emit("update:visible", false)
		emit("saved")
	} catch (err: any) {
		toast.add({ severity: "error", summary: "Error saving tooltip", detail: err?.message, life: 4000 })
	} finally {
		saving.value = false
	}
}
</script>
